import React, { useEffect, useMemo, useState } from 'react';
import { getJson } from '../api';
import singleDoor from './assets/singleDoor.jpg';
import '../styles/ProductList.css';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const formatCurrency = (value) =>
  Number.isFinite(Number(value)) ? `$${Number(value).toFixed(2)}` : '—';

function ProductList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;
    async function fetchInventory() {
      setStatus(STATUS.LOADING);
      setErrorMessage('');
      try {
        const payload = await getJson('/inventory');
        const normalizedItems = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload)
            ? payload
            : [];
        if (isMounted) {
          setItems(normalizedItems);
          setStatus(STATUS.SUCCESS);
        }
      } catch (error) {
        if (isMounted) {
          const message =
            error.status === 401 || error.status === 403
              ? 'You need an authorized account to view the product catalog.'
              : 'Unable to load the product catalog right now.';
          setItems([]);
          setErrorMessage(message);
          setStatus(STATUS.ERROR);
        }
      }
    }
    fetchInventory();
    return () => {
      isMounted = false;
    };
  }, []);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  }, [items]);

  const isOutOfStock = (value) => {
    if (value === null || value === undefined) {
      return true;
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return false;
    }
    return numericValue === 0;
  };

  return (
    <div className="productList-page">
      <header className="productList-header">
        <div>
          <p className="eyebrow">Catalog</p>
          <h1>All Inventory Products</h1>
          <p className="subhead">
            Browse every item tracked in the inventory database. Items with zero
            or unknown inventory are flagged so the sales team can set proper
            expectations with customers.
          </p>
        </div>
        <div className="productList-status">
          {status === STATUS.LOADING && 'Refreshing product data...'}
          {status === STATUS.ERROR && errorMessage}
          {status === STATUS.SUCCESS &&
            `${sortedItems.length} products available`}
        </div>
      </header>

      {status === STATUS.SUCCESS && (
        <section className="product-card-grid">
          {sortedItems.map((item) => {
            const outOfStock = isOutOfStock(item.current_inventory);
            return (
              <article className="product-card" key={item.product_number}>
                <div className="product-card-media">
                  <img
                    src={singleDoor}
                    alt={
                      item.name
                        ? `Thumbnail of ${item.name}`
                        : 'Door product thumbnail'
                    }
                    loading="lazy"
                  />
                </div>
                <div className="product-card-body">
                  <div className="product-card-heading">
                    <p className="product-number">{item.product_number}</p>
                    <h2>{item.name}</h2>
                    <p className="product-description">{item.description}</p>
                  </div>
                  <dl>
                    <div className="product-field">
                      <dt>Size</dt>
                      <dd>{item.size || '—'}</dd>
                    </div>
                    <div className="product-field">
                      <dt>Thickness</dt>
                      <dd>{item.thickness || '—'}</dd>
                    </div>
                    <div className="product-field">
                      <dt>Dealer Price</dt>
                      <dd>{formatCurrency(item.dealer_price)}</dd>
                    </div>
                    <div className="product-field">
                      <dt>Inventory</dt>
                      <dd>
                        {outOfStock ? (
                          <span className="stock-indicator out">
                            Out of Stock
                          </span>
                        ) : (
                          <span className="stock-indicator in">
                            {item.current_inventory}
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>
              </article>
            );
          })}
        </section>
      )}

      {status === STATUS.LOADING && (
        <div className="productList-banner">Loading products...</div>
      )}
      {status === STATUS.ERROR && (
        <div className="productList-banner error">{errorMessage}</div>
      )}
    </div>
  );
}

export default ProductList;
